import ConditionalToolTip from "components/design/conditionalToolTip";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { AddShareholder } from "components/Entity/Shareholders/AddShareholder";
import { Cross } from "components/icons/Cross";
import { useFormikContext } from "formik";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MultiValue, SingleValue } from "react-select";
import GrayPencil from "static/images/GrayPencil.svg";
import { useGetAllShareholdersQuery } from "store/apis/Shareholders";
import { useUpdateTaskFormDataMutation } from "store/apis/task";
import { FilingFormType, Section, SectionField } from "types/Models/Filing";
import { Shareholder } from "types/Models/Shareholders";
import { BackendError } from "types/utils/error";
import { ShareholderOption } from "./ShareholderOption";

type ShareholderAutofillType = {
  section: Section;
  groupId: string;
  entityId: string;
  formData: FilingFormType;
  field: SectionField;
};

const Info = ({ label, value }: { label: string; value: any }) => {
  return (
    <div>
      <div className="t-text-body-sm t-text-text-30">{label}</div>
      <div className="t-text-body t-text-text-60">{value || "-"}</div>
    </div>
  );
};

export const ShareholderType = {
  COMPANY_ENTITY: "Entity",
  PERSONA: "Individual",
};

export const ShareholderAutofillV2 = ({
  section,
  groupId,
  entityId,
  formData,
  field,
}: ShareholderAutofillType) => {
  const { values: formValues } = useFormikContext();
  const addModal = useModal();
  const editModal = useModal();
  const { data: shareholders = [] } = useGetAllShareholdersQuery(
    { entityId, groupId },
    { skip: !groupId || !entityId }
  );

  const { alertToast, successToast } = useToast();
  const [updateTaskForm, { isLoading: isUpdatingTask }] =
    useUpdateTaskFormDataMutation();
  const { taskId } = useParams<{ taskId: string }>();
  const [detailsForEdit, setDetailsForEdit] = useState<Shareholder | null>(
    null
  );
  const [totalShares, setTotalShares] = useState(0);

  const addField = (times: number) => {
    const repeatables = section.repeatable_fields;
    const newFields: { id: number; fields: any }[] = [];

    for (let i = 0; i < times; i++) {
      const templateFields = repeatables?.[0]?.fields.map((field: any) => {
        let keyOnly = field.field_key.split("_");
        keyOnly = keyOnly.slice(0, keyOnly.length - 1);
        keyOnly = keyOnly.join("_");
        return {
          ...field,
          value: "",
          field_key: `${keyOnly}_${i}`,
        };
      });
      newFields.push({ id: i, fields: templateFields });
    }
    return newFields;
  };

  const getPayload = (updatedSections: any) => {
    const payload = {
      form_data: {
        ...formData,
        data: {
          ...formData.data.data,
          section_group: [{ sections: updatedSections }],
        },
      },
    };
    return payload;
  };

  const handleShareholderUpdate = async (
    autofill: Shareholder,
    shareholderId: string | null,
    previousStake: string | null
  ) => {
    if (shareholderId) {
      editShareholder(autofill, shareholderId, previousStake);
    } else {
      updateTaskDetails(autofill);
    }
  };

  const shouldExcludeShareholder = (
    autofill: Shareholder,
    previousStake: number
  ): boolean => {
    const selectedShareholders = section.repeatable_fields;

    const isOwnershipStakeBelowThreshold =
      Number(autofill?.ownership_stake || 0) < 20;

    if (isOwnershipStakeBelowThreshold) {
      return true;
    }

    const totalStake =
      selectedShareholders?.reduce((acc, field) => {
        const ownershipStake = Number(
          field.selected_autofill_details?.shareholder_details
            ?.ownership_stake ?? 0
        );
        return acc + ownershipStake;
      }, 0) || 0;

    const adjustedTotalStake =
      totalStake -
      Number(previousStake || 0) +
      Number(autofill?.ownership_stake || 0);

    const isOwnershipStakeExceedingLimit = adjustedTotalStake > 100;

    return isOwnershipStakeExceedingLimit;
  };

  const editShareholder = async (
    autofill: Shareholder,
    shareholderId: string,
    previousStake: string | null
  ) => {
    try {
      const shareholders = section.repeatable_fields;

      const updatedShareholders = shareholders?.map(
        (shareholder?: {
          id: number;
          fields: any;
          selected_autofill_details?: any;
        }) => {
          if (
            shareholderId ===
            shareholder?.selected_autofill_details?.shareholder_details?.uuid
          ) {
            const editedDirector = {
              ...shareholder,
              fields: shareholder.fields?.map(
                (field: { field_key: string }) => {
                  return getField(field.field_key, field, autofill);
                }
              ),
              selected_autofill_details: {
                shareholder_details: shouldExcludeShareholder(
                  autofill,
                  Number(previousStake) || 20
                )
                  ? null
                  : autofill,
              },
            };
            return editedDirector;
          } else {
            return shareholder;
          }
        }
      );

      const updatedSections =
        updateShareholderRepeatableStep(updatedShareholders);
      const payload = getPayload(updatedSections);

      await updateTaskForm({
        formId: formData.uuid,
        payload,
        taskId,
      }).unwrap();
    } catch (e: any) {
      console.log({ e });
      alertToast({ message: e?.data?.error?.message });
    }
  };

  const updateTaskDetails = async (autofill: Shareholder) => {
    try {
      const repeatables = section.repeatable_fields || [];
      const isDataExistsInLastRepeatableField = repeatables?.[
        repeatables.length - 1
      ]?.fields?.some(
        (field: { value: any; field_key: any }) =>
          Boolean(field.value) && !field.field_key.includes("director_country")
      );
      const repeatableFieldId = isDataExistsInLastRepeatableField
        ? repeatables.length
        : Number(repeatables.length) - 1;

      const addedShareholderSectionFields = repeatables[0].fields.map(
        (field: any) => {
          let keyOnly = field.field_key.split("_");
          keyOnly = keyOnly.slice(0, keyOnly.length - 1);
          keyOnly = keyOnly.join("_");
          const fieldKey = `${keyOnly}_${repeatableFieldId}`;
          const fieldWithUpdatedKey = { ...field, field_key: fieldKey };

          return getField(
            fieldKey,
            fieldWithUpdatedKey,
            autofill as Shareholder
          );
        }
      );

      const newRepeatableField = {
        id: repeatableFieldId,
        fields: addedShareholderSectionFields,
        selected_autofill_details: {
          shareholder_details: shouldExcludeShareholder(autofill, 0)
            ? null
            : autofill,
        },
      };

      const repeatableFieldsWithValue =
        repeatableFieldId === 0
          ? [newRepeatableField]
          : [...repeatables, newRepeatableField];
      const updatedSections = updateShareholderRepeatableStep(
        repeatableFieldsWithValue
      );
      const payload = getPayload(updatedSections);

      await updateTaskForm({
        formId: formData.uuid,
        payload,
        taskId,
      }).unwrap();
      successToast({ message: "Task updated successfully!" });
    } catch (error) {
      console.log({ error });
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const updateShareholderRepeatableStep = (repeatableFieldsWithValue: any) => {
    return formData?.data?.data?.section_group?.[0].sections.map(
      (section: { [x: string]: any; section_key: string }) => {
        if (section.section_key === "shareholders") {
          return {
            ...section,
            repeatable_fields: repeatableFieldsWithValue,
          };
        }
        return {
          ...section,
          section_fields: section?.section_fields?.map(
            (sectionField: SectionField) => ({
              ...sectionField,
              value:
                //@ts-ignore
                formValues?.[sectionField.field_key] || sectionField?.value,
            })
          ),
        };
      }
    );
  };

  const getField = (fieldKey: string, field: any, valuesToSet: Shareholder) => {
    if (fieldKey.includes("shareholder_name")) {
      return {
        ...field,
        value: valuesToSet?.name,
      };
    }
    if (fieldKey.includes("shareholder_type")) {
      return { ...field, value: valuesToSet.shareholder_type };
    }
    if (fieldKey.includes("shareholder_address")) {
      return { ...field, value: valuesToSet.address?.autofill_string };
    }
    if (fieldKey.includes("shareholder_tin")) {
      return { ...field, value: valuesToSet.tin };
    }
    if (fieldKey.includes("shareholder_country")) {
      return { ...field, value: valuesToSet.country?.name };
    }
    if (fieldKey.includes("shareholder_ownership_stake")) {
      return { ...field, value: valuesToSet.ownership_stake };
    }

    //@ts-ignore
    return { ...field, value: formValues?.[fieldKey] || field.value };
  };

  const updateTaskSection = async (
    selectedOptions: SingleValue<OptionData> | MultiValue<OptionData> = []
  ) => {
    try {
      const repeatableFields = addField(
        Math.max((selectedOptions as MultiValue<OptionData>)?.length, 1)
      );

      const repeatableFieldsWithValue = repeatableFields.map(
        (repeatableField, i) => {
          const valuesToSet = shareholders.find(
            //@ts-ignore
            ({ uuid }) => uuid === selectedOptions?.[i]?.value
          );

          if (!valuesToSet) {
            return repeatableField;
          }

          const updatedFieldWithValue = {
            ...repeatableField,
            selected_autofill_details: { shareholder_details: valuesToSet },
            fields: repeatableField.fields?.map(
              (field: { field_key: string }) => {
                return getField(field.field_key, field, valuesToSet);
              }
            ),
          };
          return updatedFieldWithValue;
        }
      );

      const updatedSections = updateShareholderRepeatableStep(
        repeatableFieldsWithValue
      );

      const payload = getPayload(updatedSections);

      await updateTaskForm({
        formId: formData.uuid,
        payload,
        taskId,
      }).unwrap();
      successToast({ message: "Task updated successfully!" });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const handleDelete = async (shareholderId: string) => {
    try {
      const repeatables = section.repeatable_fields;
      const updatedShareholders = repeatables
        ?.filter(
          (shareholder) =>
            shareholder.selected_autofill_details?.shareholder_details?.uuid &&
            shareholder.selected_autofill_details.shareholder_details.uuid !==
              shareholderId
        )
        .map((shareholder) => ({
          value: shareholder.selected_autofill_details?.shareholder_details
            .uuid as string,
        }));
      if (updatedShareholders) {
        updateTaskSection(
          updatedShareholders as unknown as MultiValue<OptionData>
        );
      }
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const openEdit = (autofillDetails: Shareholder) => {
    setDetailsForEdit(autofillDetails);
    editModal.open();
  };

  const onClose = () => {
    setDetailsForEdit(null);
    addModal.close();
    editModal.close();
  };

  useEffect(() => {
    const selectedShareSum = section.repeatable_fields?.reduce(
      (acc, field) =>
        (acc += Number(
          field.selected_autofill_details?.shareholder_details
            ?.ownership_stake ?? 0
        )),
      0
    );
    setTotalShares(selectedShareSum || 0);
  }, [section.repeatable_fields]);

  const selectedShareholders = section.repeatable_fields
    ?.filter(
      (shareholder) =>
        shareholder.selected_autofill_details?.shareholder_details
    )
    ?.map(
      (shareholder) =>
        shareholder.selected_autofill_details?.shareholder_details
    );

  const selectedShareholderOptions =
    selectedShareholders && selectedShareholders?.length > 0
      ? selectedShareholders?.map((shareholder) => ({
          label: (
            <div className="t-text-body t-w-full t-truncate">
              {shareholder?.name}
            </div>
          ),
          value: shareholder?.uuid!,
        }))
      : null;

  return (
    <div>
      <Combobox
        actions={
          <Button type="button" customType="link" onClick={addModal.open}>
            Add new shareholder
          </Button>
        }
        components={{
          ClearIndicator: () => null,
          Option: (props) => (
            <ShareholderOption
              {...props}
              onEdit={(shareholderId: string) =>
                openEdit(
                  shareholders.find(
                    (shareholder) => shareholder.uuid === shareholderId
                  )!
                )
              }
            />
          ),
        }}
        menuPortalTarget={document.body}
        isMulti
        onChange={updateTaskSection}
        value={selectedShareholderOptions}
        name="shareholder"
        label={field.field_label}
        placeholder={field.placeholder}
        options={shareholders.map((shareholder) => {
          const isOwnershipStakeLessThan20 =
            Number(shareholder?.ownership_stake || 0) < 20;
          const isOwnershipStateExceed100 =
            totalShares + Number(shareholder?.ownership_stake || 0) > 100;
          const isSelected = selectedShareholders?.some(
            (selectedShareholder) =>
              selectedShareholder?.uuid === shareholder.uuid
          );

          let tooltipText = "";

          if (isOwnershipStateExceed100) {
            tooltipText =
              "Total shareholders' shares cannot exceed 100%. Please adjust existing share percentages before adding more shareholders.";
          }
          if (isOwnershipStakeLessThan20) {
            tooltipText =
              "Only owners with more than 20% ownership need to be added.";
          }

          const disableOption =
            (isOwnershipStakeLessThan20 || isOwnershipStateExceed100) &&
            !isSelected;

          return {
            label: (
              <div className="t-text-body t-w-full t-truncate">
                <ConditionalToolTip condition={disableOption && tooltipText}>
                  <div className="t-w-fit t-flex t-items-center t-gap-2">
                    <div>{shareholder.name}</div>
                    <div className="t-bg-neutral-0 t-px-3 t-py-0.5 t-rounded-3xl t-text-caption">
                      {shareholder.ownership_stake}% Share
                    </div>
                  </div>
                </ConditionalToolTip>
              </div>
            ),
            value: shareholder.uuid,
            disableOption: disableOption,
          };
        })}
        isLoading={isUpdatingTask}
        tooltipText={field.tooltip}
      />
      <div className="t-mt-6">
        {section.repeatable_fields
          ?.filter(
            (repeatableField) =>
              repeatableField.selected_autofill_details?.shareholder_details
          )
          ?.map(
            (repeatableField: {
              id: number;
              fields: any[];
              selected_autofill_details?: {
                shareholder_details: Shareholder;
              };
            }) => {
              const isOneShareholderRemaining =
                section.repeatable_fields?.length === 1;

              return (
                <div className="t-mb-3" key={repeatableField?.id}>
                  <div className="t-border t-border-solid t-border-neutral-0 t-p-4 t-rounded-lg">
                    <div className="t-flex t-justify-between">
                      <div className="t-flex t-items-center">
                        <div className="t-text-subtext t-text-text-100">
                          {
                            repeatableField?.selected_autofill_details
                              ?.shareholder_details?.name
                          }
                        </div>
                        {repeatableField?.selected_autofill_details
                          ?.shareholder_details?.ownership_stake && (
                          <div className="t-text-body-sm t-text-text-30 t-ml-1">
                            (
                            {
                              repeatableField?.selected_autofill_details
                                ?.shareholder_details?.ownership_stake
                            }
                            % Share)
                          </div>
                        )}
                      </div>
                      <div className="t-flex t-gap-2">
                        <Button
                          onClick={() =>
                            openEdit(
                              repeatableField?.selected_autofill_details
                                ?.shareholder_details!
                            )
                          }
                          customType="transparent"
                          size="small"
                          type="button"
                        >
                          <img
                            src={GrayPencil}
                            className="t-h-4 t-m-1"
                            alt="GrayPencil"
                          />
                        </Button>
                        {!isOneShareholderRemaining && (
                          <Button
                            onClick={() =>
                              handleDelete(
                                repeatableField?.selected_autofill_details
                                  ?.shareholder_details?.uuid!
                              )
                            }
                            customType="transparent"
                            size="small"
                            type="button"
                          >
                            <span className="t-text-text-30">
                              <Cross />
                            </span>
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="t-flex t-mt-3">
                      <div className="t-flex-1">
                        <Info
                          label="TIN"
                          value={
                            repeatableField?.selected_autofill_details
                              ?.shareholder_details?.tin
                          }
                        />
                      </div>
                      <div className="t-flex-1">
                        <Info
                          label="Citizenship"
                          value={
                            repeatableField?.selected_autofill_details
                              ?.shareholder_details?.country?.name
                          }
                        />
                      </div>
                    </div>
                    <div className="t-mt-3">
                      <Info
                        label="Address"
                        value={
                          repeatableField?.selected_autofill_details
                            ?.shareholder_details?.address?.autofill_string
                        }
                      />
                    </div>
                  </div>
                </div>
              );
            }
          )}
      </div>
      <AddShareholder
        isOpen={addModal.isOpen || editModal.isOpen}
        close={onClose}
        shareholder={detailsForEdit}
        entityId={entityId}
        onSuccess={handleShareholderUpdate}
      />
    </div>
  );
};
